import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  AfterContentInit,
  Component,
  ContentChildren,
  HostBinding,
  input,
  Input,
  OnChanges,
  QueryList,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { ErrorDialogComponent } from '@app/shared/components/error-dialog/error-dialog.component';
import { DialogService } from '@app/shared/dialog/dialog.service';
import { TranscludeDirective } from '@app/shared/directives/transclude.directive';
import { PortalError } from '@app/utils/error/portal-error';
import { watch } from '@app/utils/signals/watch';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'portal-errors',
  templateUrl: './errors.component.html',
  styles: [],
  host: {
    class: 'errors',
  },
  standalone: false,
})
export class ErrorsComponent implements AfterContentInit, OnChanges {
  @Input()
  alertClass: string = '';

  open = input(false, { transform: coerceBooleanProperty });

  @Input()
  dismissible: boolean = true;

  @Input()
  errors!: PortalError[] | PortalError;

  /**
   * Display as alert or as plain text with an additional dialog that explains the error.
   * The plain variant is useful for explaining errors when there is little space available.
   */
  @Input()
  variant: 'alert' | 'plain' = 'alert';

  @ContentChildren(TranscludeDirective)
  transclusions: QueryList<TranscludeDirective>;

  collapsed: boolean = true;
  internalErrors: PortalError[] = [];
  summary?: TemplateRef<any>;
  title?: TemplateRef<any>;
  visible: boolean = true;

  @HostBinding('class.d-none')
  get hasNoErrors() {
    return this.internalErrors.length === 0;
  }

  constructor(private dialogService: DialogService) {
    watch([this.open], ([open]) => {
      this.collapsed = !open;
    });
  }

  ngAfterContentInit() {
    this.summary = this.transclusions.find((item) => item.id === 'summary')?.templateRef;
    this.title = this.transclusions.find((item) => item.id === 'title')?.templateRef;

    this.transclusions.changes.pipe(untilDestroyed(this)).subscribe((transclusions) => {
      this.summary = transclusions.find((item) => item.id === 'summary')?.templateRef;
      this.title = transclusions.find((item) => item.id === 'title')?.templateRef;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('errors' in changes) {
      const errors = changes.errors.currentValue as PortalError | PortalError[];

      if (Array.isArray(errors)) {
        this.internalErrors = errors.filter((error) => error != null);
      } else if (errors != null) {
        this.internalErrors = [errors];
      } else {
        this.internalErrors = [];
      }

      this.visible = true;
    }
  }

  hide(): void {
    this.visible = false;
  }

  showDetails(): void {
    this.dialogService
      .open(ErrorDialogComponent, {
        size: 'lg',
      })
      .withInput({
        errors: this.internalErrors,
        title: this.title,
      });
  }
}
